import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Avatar,
  Stack,
  Paper,
  Alert,
  TextField,
  Select,
  MenuItem,
  Input,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import { FileText, Loader2 } from 'lucide-react';
import { updateDocument } from '../../services/documentAPIs';

const DocumentDetailsModal = ({
  selectedDocument,
  setSelectedDocument,
  fetchDocuments,
  isEditMode,
  setIsEditMode,
}) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const documentTypes = [
    'Contract',
    'National ID',
    'Diploma',
    'Payroll',
    'Personal Documents',
    'Code of Conduct',
    'Probation Period Agreement',
    'Bank Details Form',
    'Promotion Letters',
    'Other',
  ];

  const roleOptions = ['DIRECTOR', 'HR', 'EMPLOYEE', 'MANAGER'];

  useEffect(() => {
    if (selectedDocument) {
      setFormData({ ...selectedDocument });
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (selectedDocument && !isEditMode) {
      setFormData({ ...selectedDocument });
    }
  }, [selectedDocument, isEditMode]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({ ...formData, file: file, fileName: file.name });
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const fD = new FormData();
      const documentData = {
        employee: { employeeId: formData.employee.employeeId },
        type: formData.type,
        description: formData.description,
        visibleToRoles: formData.visibleToRoles,
        fileName: formData.fileName,
      };
      fD.append('document', new Blob([JSON.stringify(documentData)], { type: 'application/json' }));
      if (formData.file) {
        fD.append('file', formData.file);
      }

      const updated = await updateDocument(formData.id, fD);
      setSelectedDocument(updated);
      setSuccessMessage('Document updated successfully');
      fetchDocuments();
      setIsEditMode(false);
    } catch (error) {
      console.error('Update failed:', error);
      setErrorMessage(error?.response?.data?.message || error.message || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!loading) {
      setSelectedDocument(null);
      setIsEditMode(false);
      setSuccessMessage('');
      setErrorMessage('');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Open email client with employee's email
  const handleEmailEmployee = () => {
    const email = formData.employee?.user?.email;
    if (email) {
      window.location.href = `mailto:${encodeURIComponent(email)}`;
    } else {
      setErrorMessage('Employee email not available');
    }
  };

  const handleExportDoc = () => {
    if (!formData.filePath) {
      setErrorMessage('Document file not available for download');
      return;
    }

    const fileUrl = formData.filePath;
    const fileName = formData.fileName || 'document';

    // Open a new blank window
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      setErrorMessage('Please allow pop-ups to download the file.');
      return;
    }

    // Inject a script to auto-download the file in the new window
    newWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Downloading Document...</title>
    </head>
    <body>
      <script>
        // Create and trigger download link
        const link = document.createElement('a');
        link.href = '${fileUrl.replace(/'/g, "\\'")}';
        link.download = '${fileName.replace(/'/g, "\\'")}';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Optional: close the window after download starts
        // setTimeout(() => window.close(), 1000);
      </script>
      <p style="font-family: sans-serif; text-align: center; margin-top: 40px;">
        If your download doesn't start automatically, 
        <a href="${fileUrl}" download="${fileName}" style="color: #1976d2; text-decoration: none;">click here</a>.
      </p>
    </body>
    </html>
  `);
    newWindow.document.close();
  };

  if (!selectedDocument) return null;



  return (
    <Dialog
      open={Boolean(selectedDocument)}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !loading) {
          handleDialogClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        className: "form-modal",
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          ...(isEditMode && {
            border: '2px solid #ff4d4f',
            animation: 'pulse 2s infinite',
          }),
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2" fontWeight="600">
            Document Details
          </Typography>
          <IconButton
            onClick={handleDialogClose}
            disabled={loading}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 2,
          maxHeight: '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                sx={{ bgcolor: 'primary.main', mr: 2 }}
              >
                <PersonIcon />
              </Avatar>
              <Typography variant="h6" fontWeight="600">
                Employee Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Full Name
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formData.employee?.firstName} {formData.employee?.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Employee position
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formData.employee?.position}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid item sx={{ width: '60%' }}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <FileCopyIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="600">
                  Document Details
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    File Name
                  </Typography>
                  {isEditMode ? (
                    <Box>
                      <Typography variant="body2" fontWeight="500" mb={1}>
                        {formData.fileName || 'No file selected'}
                      </Typography>
                      <Button
                        component="label"
                        className="doc-submit-button"
                        disabled={loading}
                        startIcon={loading ? <Loader2 className="doc-button-icon doc-loader" /> : <FileText className="doc-button-icon" />}
                        sx={{
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          '&:hover': { backgroundColor: '#115293' },
                          borderRadius: 1,
                          padding: '8px 16px',
                        }}
                      >
                        {loading ? 'Uploading...' : 'Upload Document'}
                        <Input
                          type="file"
                          hidden
                          onChange={handleFileChange}
                          inputProps={{ accept: '.pdf,.jpg,.jpeg,.png' }}
                        />
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2" fontWeight="500">
                      {formData.fileName}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  ) : (
                    <Typography variant="body2" fontWeight="500">
                      {formData.description}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  {isEditMode ? (
                    <Select
                      name="type"
                      fullWidth
                      value={formData.type || ''}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      {documentTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Chip
                      label={formData.type}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Visible to Roles
                  </Typography>
                  {isEditMode ? (
                    <Select
                      name="visibleToRoles"
                      multiple
                      fullWidth
                      value={formData.visibleToRoles || []}
                      onChange={(e) => setFormData({ ...formData, visibleToRoles: e.target.value })}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {roleOptions.map(role => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Box>
                      {formData.visibleToRoles?.map(role => (
                        <Chip key={role} label={role} size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Uploaded By
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formData.uploadedBy?.firstName} {formData.uploadedBy?.lastName}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Upload Date
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatDate(formData.uploadDate)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {formData.filePath && (
            <Card elevation={1} sx={{ borderRadius: 2, width: '37%', padding: 0 }}>
              <CardContent sx={{ margin: 0, padding: 0, height: '100%' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom ml={'auto'} mr={'auto'} mt={1} mb={1} textAlign="center">
                  Document File
                </Typography>
                {formData.filePath.endsWith('.pdf') ? (
                  <embed
                    src={formData.filePath}
                    type="application/pdf"
                    width="100%"
                    height="300px"
                  />
                ) : (
                  <img
                    src={formData.filePath}
                    alt={formData.fileName}
                    style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
            {successMessage}
          </Alert>
        )}
        {!isEditMode ? (
          <Box display="flex" gap={1} mr="auto">
            <Button
              variant="contained"
              onClick={() => setIsEditMode(true)}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          </Box>
        ) : (
          <Box display="flex" gap={1} mr="auto">
            <Button
              variant="contained"
              color="success"
              onClick={handleUpdate}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditMode(false);
                setFormData({ ...selectedDocument });
              }}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Cancel Edit
            </Button>
          </Box>
        )}
        <Button
          variant="outlined"
          startIcon={<EmailIcon />}
          sx={{ borderRadius: 2 }}
          disabled={loading}
          onClick={handleEmailEmployee}
        >
          Email Employee
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 2 }}
          disabled={loading}
          onClick={handleExportDoc}
        >
          Export Doc
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDetailsModal;
